import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  DatePicker,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Segmented,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from "antd";
import dayjs from "dayjs";
import LoadingCard from "components/LoadingCard/LoadingCard";
import ListSearch from "components/ListSearch/ListSearch";
import { useTextFilter } from "helpers/textFilter";
import { CONTROL } from "pages/MarketingPages/FieldInput";
import { LANGUAGES } from "pages/MarketingPages/languages";
import { bannerSummary, windowLabel } from "./bannerState";
import {
  createBanner,
  deleteBanner,
  errorMessage,
  fetchBanners,
  setBannerStatus,
  updateBanner,
} from "./promoBannersApi";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const bannerSearchFields = (banner) => [banner.text, banner.link?.href, banner.link?.label];

/**
 * The promo strip above the site header.
 *
 * Unlike marketing pages, this one reaches the site *without a rebuild* — the
 * strip is fetched by the visitor's browser, so publishing here is live within
 * a minute (the API caches the public read for 60s). That is the whole point
 * of the feature and worth saying on screen, because every other editor in
 * this admin waits for a deploy.
 *
 * Switching the strip off is a banner too, not a setting beside them: there is
 * one rule for what the site shows — newest published banner inside its window
 * — and "show nothing" is one of the things a banner can say. It schedules
 * like any other, so the strip can be off for a fortnight and come back on its
 * own.
 */
export default function PromoBanners() {
  const [lang, setLang] = useState("bg");
  const [banners, setBanners] = useState([]);
  const [liveId, setLiveId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [isHidden, setIsHidden] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [form] = Form.useForm();

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const { banners: rows, liveId: live } = (await fetchBanners(lang)) || {};
      setBanners(rows || []);
      setLiveId(live ?? null);
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    load();
  }, [load]);

  const isNew = editing != null && !editing._id;

  const openEditor = (banner, hidden = false) => {
    setEditing(banner ?? {});
    setIsHidden(banner ? Boolean(banner.hidden) : hidden);
    form.setFieldsValue({
      text: banner?.text ?? "",
      linkHref: banner?.link?.href ?? "",
      linkLabel: banner?.link?.label ?? "",
      window:
        banner?.startsAt || banner?.endsAt
          ? [
              banner.startsAt ? dayjs(banner.startsAt) : null,
              banner.endsAt ? dayjs(banner.endsAt) : null,
            ]
          : null,
    });
  };

  const closeEditor = () => {
    setEditing(null);
    setIsHidden(false);
    form.resetFields();
  };

  const submit = async (values) => {
    setIsSaving(true);
    try {
      const [startsAt, endsAt] = values.window ?? [null, null];
      const payload = {
        lang,
        hidden: isHidden,
        // Kept even when hiding, so an editor can switch the strip back on
        // with the copy they already wrote.
        text: values.text || "",
        link: { href: values.linkHref || "", label: values.linkLabel || "" },
        // null rather than undefined: clearing the dates has to reach the API
        // as a value, or a scheduled banner can never become permanent again.
        startsAt: startsAt ? startsAt.toISOString() : null,
        endsAt: endsAt ? endsAt.toISOString() : null,
      };

      if (isNew) await createBanner(payload);
      else await updateBanner(editing._id, payload);

      message.success("Запазено");
      closeEditor();
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Every mutation refetches rather than splicing the returned document into
   * state: the row's "Live now" flag is the server's answer, and a local
   * update would have to recompute it here — which is exactly the duplication
   * this screen avoids.
   */
  const changeStatus = async (banner, status) => {
    try {
      await setBannerStatus(banner._id, status);
      message.success(status === "published" ? "Публикувано на сайта" : "Свалено от сайта");
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    }
  };

  const visibleBanners = useTextFilter(banners, bannerSearchFields, searchQuery);

  const remove = async (banner) => {
    try {
      await deleteBanner(banner._id);
      message.success("Изтрито");
      await load();
    } catch (error) {
      message.error(errorMessage(error));
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 720, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Промо лента
        </Title>
        <Text type="secondary">
          Тъмната лента над хедъра на сайта. Публикуваното тук се появява на сайта до минута,
          без да чакаш обновяване. Ако няма публикувана лента, сайтът показва вградения си текст.
        </Text>
      </Space>

      <Segmented
        options={LANGUAGES}
        value={lang}
        onChange={setLang}
        block
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" size={8} style={{ width: "100%", marginBottom: 16 }}>
        <Button type="primary" block style={CONTROL} onClick={() => openEditor(null)}>
          Нова лента
        </Button>
        <Button block style={CONTROL} onClick={() => openEditor(null, true)}>
          Скрий лентата
        </Button>
      </Space>

      {isLoading && <LoadingCard />}

      {!isLoading && !loadError && (
        <ListSearch
          value={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Търсене по текст"
          className="mb-3"
        />
      )}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на лентите"
          description={loadError}
          action={<Button onClick={load}>Опитай отново</Button>}
        />
      )}

      {!isLoading && !loadError && (
        <List
          bordered
          dataSource={visibleBanners}
          locale={{
            emptyText: searchQuery
              ? "Няма ленти, отговарящи на търсенето."
              : "Все още няма ленти — сайтът показва вградения си текст.",
          }}
          renderItem={(banner) => (
            <List.Item style={{ display: "block", padding: 16 }}>
              <Space direction="vertical" size={8} style={{ width: "100%" }}>
                <Space wrap size={8}>
                  {banner._id === liveId ? (
                    <Tag color="green">На живо</Tag>
                  ) : banner.status === "published" ? (
                    <Tag color="blue">Публикувано</Tag>
                  ) : (
                    <Tag>Чернова</Tag>
                  )}
                  {banner.hidden && <Tag color="orange">Скрива лентата</Tag>}
                  <Text type="secondary">{windowLabel(banner)}</Text>
                </Space>

                <Text strong={!banner.hidden} type={banner.hidden ? "secondary" : undefined}>
                  {bannerSummary(banner)}
                </Text>
                {!banner.hidden && banner.link?.href && (
                  <Text type="secondary">{banner.link.href}</Text>
                )}

                <Space wrap size={8}>
                  <Button style={CONTROL} onClick={() => openEditor(banner)}>
                    Редактирай
                  </Button>
                  {banner.status === "published" ? (
                    <Button style={CONTROL} onClick={() => changeStatus(banner, "draft")}>
                      Свали от сайта
                    </Button>
                  ) : (
                    <Button type="primary" style={CONTROL} onClick={() => changeStatus(banner, "published")}>
                      Публикувай
                    </Button>
                  )}
                  <Popconfirm
                    title="Сигурен ли си, че искаш да изтриеш тази лента?"
                    onConfirm={() => remove(banner)}
                    okText="Да"
                    cancelText="Отказ"
                  >
                    <Button danger style={CONTROL}>
                      Изтрий
                    </Button>
                  </Popconfirm>
                </Space>
              </Space>
            </List.Item>
          )}
        />
      )}

      <Modal
        open={editing != null}
        title={isNew ? "Нова лента" : "Редактиране на лента"}
        onCancel={closeEditor}
        onOk={form.submit}
        confirmLoading={isSaving}
        okText="Запази"
        cancelText="Отказ"
        // Saving never publishes — the list's Publish button does that.
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={submit} requiredMark={false}>
          <Form.Item label="Скрий лентата напълно">
            <Space size={8}>
              <Switch checked={isHidden} onChange={setIsHidden} />
              <Text type="secondary">
                {isHidden
                  ? "Докато е активно, сайтът изобщо не показва лента."
                  : "Изключено — тази лента показва текста по-долу."}
              </Text>
            </Space>
          </Form.Item>

          <Form.Item
            name="text"
            label="Текст"
            rules={
              isHidden
                ? []
                : [
                    { required: true, whitespace: true, message: "Лентата се нуждае от текст" },
                    { max: 200, message: "Лентата е един ред — до 200 символа" },
                  ]
            }
            extra={
              isHidden
                ? "Запазено, но не се показва, за да можеш да включиш лентата отново с него."
                : "Един ред. Дългият текст се отрязва на сайта, вместо да пренася."
            }
          >
            <Input.TextArea rows={2} maxLength={200} showCount disabled={isHidden} />
          </Form.Item>

          <Form.Item
            name="linkHref"
            label="Връзка (незадължително)"
            extra="Прави цялата лента кликаема."
          >
            <Input placeholder="https://…" disabled={isHidden} />
          </Form.Item>

          <Form.Item
            name="linkLabel"
            label="Описание на връзката (незадължително)"
            extra="Прочита се от екранни четци вместо текста на лентата."
          >
            <Input disabled={isHidden} />
          </Form.Item>

          <Form.Item
            name="window"
            label="Показвай между (незадължително)"
            extra="Остави празно за лента без крайна дата."
          >
            <RangePicker style={{ width: "100%" }} allowEmpty={[true, true]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
